import React from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import BlackoutReasonChip from "ui/Blackout/BlackoutReasonChip"
import BookingsActionAlert from "ui/Blackout/BookingsActionAlert"
import { buildReservationsLink, openReservationsLink } from "helpers/blackouts"
import { buildBlackoutRuns, getDayChipProps, resolveChipLabel } from "pagesV2/pro/Calendar/blackoutUtils"
import { useCalendarBlackouts } from "pagesV2/pro/Calendar/hooks/useCalendarBlackouts"
import styles from "./index.module.css"

/**
 * Blackout chips for the Crew and Equipment week grids.
 *
 * Those two grids are a parallel implementation of the week — resources down the side, days
 * across the top — so none of `WeekGrid`'s blackout work reaches them, and a closure the month
 * view shows plainly was invisible there. This is that treatment, shaped for a table whose first
 * column is a resource name rather than an hour.
 *
 * @param {Object[]} calendarDays the week's days as moments
 */
/**
 * A full closure shades the column dark and a partial one light grey, exactly as the All Bookings
 * week does — so a closed day reads as closed inside the grid, not only in the bar above it.
 *
 * Exported because the day grid shades with the same two classes; keeping one source of truth
 * stops the three views drifting to three different greys.
 */
export const getBlackoutShadeClassName = (dayBlackout) => {
  if (!dayBlackout) return ""
  return dayBlackout.status === "full" ? styles.shadeFull : styles.shadePartial
}

export const useResourceWeekBlackouts = (calendarDays) => {
  const { getDay: getDayBlackout } = useCalendarBlackouts()

  const dayKeys = calendarDays.map((day) => day.format("YYYY-MM-DD"))
  const runs = buildBlackoutRuns(dayKeys, getDayBlackout)
  const hasAnyBlackout = runs.some((run) => run.day)

  /** The week's own action count, summed across its days — see AgendaWeekView for the same rule. */
  const bookingsRequiringAction = dayKeys.reduce((total, dayKey) => {
    const count = getDayBlackout(dayKey)?.bookings_requiring_action
    return total + (typeof count === "number" ? count : 0)
  }, 0)

  const reviewWeek = () =>
    openReservationsLink(buildReservationsLink({ dateFrom: dayKeys[0], dateTo: dayKeys[dayKeys.length - 1] }))

  /** The shading runs every row, filler rows included, so it reads as a closed column rather
      than a stripe behind the first resource. */
  const getShadeClassName = (dayKey) => getBlackoutShadeClassName(getDayBlackout(dayKey))

  return { runs, hasAnyBlackout, bookingsRequiringAction, reviewWeek, getShadeClassName }
}

/** The week's "N bookings require cancellation or rescheduling" banner, above the table. */
export const ResourceWeekActionAlert = ({ count, onReview }) =>
  count > 0 ? <BookingsActionAlert count={count} onReview={onReview} className={styles.actionAlert} /> : null

ResourceWeekActionAlert.propTypes = {
  count: PropTypes.number,
  onReview: PropTypes.func
}

/**
 * One `<tr>` of chips above the resource rows, a chip per run of days sharing the same closure so
 * a multi-day blackout reads as a single bar.
 */
const ResourceWeekBlackoutRow = ({ runs, leadCellClassName, onOpenDayPanel }) => {
  const { t } = useTranslation()
  return (
    // Inline height rather than a class: both grids give every `tbody tr` an even 25% share, and
    // this row must only be as tall as its chips.
    // The lead cell holds nothing, so it drops its bottom line — otherwise the row reads as an
    // empty box above the resources. The column lines stay, so the grid reads as one table.
    <tr style={{ height: 1 }}>
      <td className={`${leadCellClassName} ${styles.emptyLeadCell}`} />
      {runs.map((run) => {
        const chip = run.day && getDayChipProps(run.day)
        return (
          <td key={run.dateKey} colSpan={run.span} className={styles.chipCell}>
            {chip && (
              <BlackoutReasonChip
                label={resolveChipLabel(chip, t)}
                variant={chip.variant}
                count={chip.count}
                showCount={chip.showCount}
                onClick={onOpenDayPanel ? () => onOpenDayPanel(run.dateKey) : undefined}
              />
            )}
          </td>
        )
      })}
    </tr>
  )
}

ResourceWeekBlackoutRow.propTypes = {
  /** Runs from `buildBlackoutRuns` — each carries `dateKey`, `span` and the day's blackout. */
  runs: PropTypes.array.isRequired,
  /** Class for the leading cell, so the chips line up with the day columns. */
  leadCellClassName: PropTypes.string,
  onOpenDayPanel: PropTypes.func
}

export default ResourceWeekBlackoutRow
